import { useEffect, useState } from "react";
import { CloudSun, Umbrella } from "lucide-react";
import api from "../../lib/api";
import { WxIcon, WX_DESC } from "../Weather";

/* Trip Weather Outlook — live daily forecast per stop so packing matches the skies. */
const day = (iso) => new Date(iso + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric" });

export default function TripWeather({ trip }) {
  const [data, setData] = useState(null);
  const stops = trip.stops || [];

  useEffect(() => {
    if (!stops.length) return;
    api.get("/weather/trip", { params: { places: stops.join(","), start: trip.start_date || undefined, end: trip.end_date || undefined } })
      .then((r) => setData(r.data)).catch(() => setData({ stops: [] }));
  }, [trip.id, trip.start_date, trip.end_date, stops.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!stops.length) return null;
  const good = (data?.stops || []).filter((s) => !s.error && s.days?.length);
  if (data && !good.length) return null;
  const hints = good.flatMap((s) => s.hints || []);
  const beyond = good.some((s) => s.beyond_forecast);

  return (
    <section className="mb-10" data-testid="trip-weather">
      <h2 className="font-heading font-900 text-sky-600 text-2xl flex items-center gap-2 mb-1">
        <CloudSun className="w-6 h-6" /> Weather outlook
      </h2>
      <p className="text-xs text-muted-foreground mb-4">
        {beyond ? "Your trip is beyond the ~16-day forecast window — showing the coming week as a seasonal hint." : "Live forecast for your trip dates."}
      </p>
      {!data ? <p className="text-sm text-muted-foreground">Loading forecast…</p> : (
        <>
          {hints.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3" data-testid="trip-weather-hints">
              {hints.map((h) => (
                <span key={h} className="inline-flex items-center gap-1 bg-amber-50 border border-amber-300 text-amber-800 text-xs font-600 px-2 py-1">
                  <Umbrella className="w-3 h-3" /> {h}
                </span>
              ))}
            </div>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {good.map((s) => (
              <div key={s.place} className="border border-zinc-200 bg-white" data-testid={`trip-weather-stop-${s.place}`}>
                <div className="px-3 py-2 border-b border-zinc-100 bg-sky-50/60">
                  <span className="font-heading font-900 text-sm">{s.resolved || s.place}</span>
                  {s.country && <span className="text-xs text-muted-foreground ml-1.5">{s.country}</span>}
                </div>
                <ul className="divide-y divide-zinc-50">
                  {s.days.map((d) => (
                    <li key={d.date} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                      <span className="w-14 text-xs font-mono text-muted-foreground shrink-0">{day(d.date)}</span>
                      <WxIcon code={d.code} className="w-4 h-4 text-sky-600 shrink-0" />
                      <span className="text-xs text-zinc-600 truncate">{WX_DESC[d.code] || ""}</span>
                      <span className="ml-auto font-mono text-xs tabular-nums shrink-0">
                        <b>{d.hi != null ? Math.round(d.hi) : "–"}°</b> / {d.lo != null ? Math.round(d.lo) : "–"}°C
                        {(d.rain_pct || 0) >= 30 && <span className="text-sky-600 ml-1.5">{d.rain_pct}%☂</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
